import { useState } from "react";
import { toast } from "react-toastify";
import HeaderSection from "../../components/HeaderSection";
import { createCandidate } from "../../http";

const CreateCandidate = () => {
  const initialState = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    experienceYears: "",
    appliedFor: "",
    resumeUrl: "",
    source: "REFERRAL"
  };

  const [formData, setFormData] = useState(initialState);

  const inputEvent = (e) => {
    const { name, value } = e.target;
    setFormData((old) => ({
      ...old,
      [name]: value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    
    const {
      firstName,
      lastName,
      email,
      phone,
      experienceYears,
      appliedFor,
      resumeUrl,
      source
    } = formData;

    // Validate required fields
    if (!firstName || !lastName || !email || !phone || !experienceYears || !appliedFor || !resumeUrl || !source) {
      return toast.error("Please fill in all required fields");
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return toast.error("Please enter a valid email address");
    }

    // Validate phone number (10 digits)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phone)) {
      return toast.error("Please enter a valid 10-digit phone number");
    }

    // Validate experience (non-negative number)
    if (isNaN(experienceYears) || experienceYears < 0) {
      return toast.error("Experience years must be a non-negative number");
    }

    try {
      const response = await createCandidate(formData);
      
      console.log("Create candidate response:", response); // Debug log
      
      // Handle success response - 201 status code indicates successful creation
      if (response.status === 201 || response.success === true || response.status === 'success' || response.message?.includes('created')) {
        toast.success("Candidate created successfully!");
        setFormData({ ...initialState }); // Clear form
      } else {
        // Handle case where response indicates error but no exception thrown
        console.log("Response treated as error:", response);
        toast.error(response.message || "Failed to create candidate");
      }
    } catch (error) {
      console.error("Create candidate error:", error);
      
      // Handle different error types
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Internal server error occurred");
      }
    }
  };

  return (
    <div className="main-content">
      <section className="section">
        <HeaderSection title="Create Candidate" />
        <div className="card">
          <div className="card-body pr-5 pl-5 m-1">
            <form className="row" onSubmit={onSubmit}>
              <div className="col-md-12">
                <h4 className="text-primary">Candidate Information</h4>
                <hr />
              </div>

              <div className="form-group col-md-6">
                <label>
                  First Name <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-user"></i>
                    </div>
                  </div>
                  <input
                    onChange={inputEvent}
                    value={formData.firstName}
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="form-control"
                    placeholder="Enter first name"
                    required
                  />
                </div>
              </div>

              <div className="form-group col-md-6">
                <label>
                  Last Name <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-user"></i>
                    </div>
                  </div>
                  <input
                    onChange={inputEvent}
                    value={formData.lastName}
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="form-control"
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="form-group col-md-6">
                <label>
                  Email Address <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-envelope"></i>
                    </div>
                  </div>
                  <input
                    onChange={inputEvent}
                    value={formData.email}
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>

              <div className="form-group col-md-6">
                <label>
                  Phone Number <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-phone"></i>
                    </div>
                  </div>
                  <input
                    onChange={inputEvent}
                    value={formData.phone}
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-control"
                    placeholder="Enter 10-digit phone number"
                    maxLength="10"
                    pattern="[0-9]{10}"
                    required
                  />
                </div>
              </div>

              <div className="form-group col-md-6">
                <label>
                  Experience (Years) <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-briefcase"></i>
                    </div>
                  </div>
                  <input
                    onChange={inputEvent}
                    value={formData.experienceYears}
                    type="number"
                    id="experienceYears"
                    name="experienceYears"
                    className="form-control"
                    placeholder="Enter years of experience"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="form-group col-md-6">
                <label>
                  Source <span className="text-danger">*</span>
                </label>
                <select
                  name="source"
                  onChange={inputEvent}
                  value={formData.source}
                  className="form-control select2"
                  required
                >
                  <option value="REFERRAL">Referral</option>
                  <option value="HEADHUNTING">Headhunting</option>
                  <option value="CAREER_PAGE">Career Page</option>
                  <option value="LINKEDIN">LinkedIn</option>
                  <option value="JOB_PORTAL">Job Portal</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div className="form-group col-md-6">
                <label>
                  Job Requisition ID <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-id-badge"></i>
                    </div>
                  </div>
                  <input
                    onChange={inputEvent}
                    value={formData.appliedFor}
                    type="text"
                    id="appliedFor"
                    name="appliedFor"
                    className="form-control"
                    placeholder="Enter job requisition ID"
                    required
                  />
                </div>
              </div>

              <div className="form-group col-md-6">
                <label>
                  Resume URL <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-file-pdf"></i>
                    </div>
                  </div>
                  <input
                    onChange={inputEvent}
                    value={formData.resumeUrl}
                    type="url"
                    id="resumeUrl"
                    name="resumeUrl"
                    className="form-control"
                    placeholder="Enter resume URL"
                    required
                  />
                </div>
              </div>

              <div className="form-group col-md-12 text-center mt-4">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  style={{ width: "30vh" }}
                >
                  Create Candidate
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreateCandidate;